import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { SortType } from '../../ColumnHeader';
import { deleteSavedPlayerList, searchSavedPlayerLists } from '../../../actions/saved-lists-actions';
import { POWER_UPGRADE_SUCCESS_EVENT } from '../../../utils/powerEvents';
import { SAVED_LISTS_CHANGED_EVENT } from '../../../utils/savedLists';
import { SAVED_LISTS_PAGE_SIZE, SavedListOrderByRule } from './constants';

const normalizeList = (list, index) => ({
    id: list.id ?? list.listId ?? `no-id-${index}`,
    name: list.name ?? list.listName ?? '',
    isDefault: !!(list.isDefault ?? list.isDefaultList),
    playerCount: list.playerCount ?? 0,
    recentActivity: !!list.recentActivity,
});

const toOrderByRule = sortType => (sortType === SortType.ASC
    ? SavedListOrderByRule.ASCENDING
    : SavedListOrderByRule.DESCENDING);

const useSavedPlayerLists = () => {
    const dispatch = useDispatch();
    const [searchValue, setSearchValue] = useState('');
    const [sortColumn, setSortColumn] = useState(null);
    const [sortType, setSortType] = useState(null);
    const [page, setPage] = useState(1);
    const [lists, setLists] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalPlayerCount, setTotalPlayerCount] = useState(0);
    const [accessStatus, setAccessStatus] = useState(null);
    const [shouldShowNotificationOptIn, setShouldShowNotificationOptIn] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    // Monotonic token so only the newest in-flight request applies its response — a fast
    // sort/search/page change can otherwise let a slower earlier request resolve last and paint
    // stale rows under an already-updated page indicator.
    const requestIdRef = useRef(0);

    const orderByRules = sortColumn && sortType ? toOrderByRule(sortType) : null;

    const fetchLists = useCallback(() => {
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        setIsLoading(true);
        setIsError(false);
        return dispatch(searchSavedPlayerLists({
            search: searchValue,
            orderBy: sortColumn || undefined,
            orderByRules: orderByRules || undefined,
            page,
            size: SAVED_LISTS_PAGE_SIZE,
        }))
            .then((result) => {
                if (requestId !== requestIdRef.current) {
                    return;
                }
                const data = result?.data || {};
                const rawLists = data.items ?? [];
                setLists(rawLists.map(normalizeList));
                setTotal(data.total ?? 0);
                setTotalPages(data.totalPages ?? Math.ceil((data.total ?? 0) / SAVED_LISTS_PAGE_SIZE));
                setTotalPlayerCount(data.totalPlayerCount ?? 0);
                setAccessStatus(data.savedListsAccessStatus ?? null);
                setShouldShowNotificationOptIn(!!data.shouldShowNotificationOptIn);
                setIsLoading(false);
            })
            .catch(() => {
                if (requestId !== requestIdRef.current) {
                    return;
                }
                // Reset the result fields (not membership status) so a failed refetch shows a
                // consistent empty result instead of stale rows/counts contradicting each other.
                setLists([]);
                setTotal(0);
                setTotalPages(0);
                setIsError(true);
                setIsLoading(false);
            });
    }, [dispatch, searchValue, sortColumn, orderByRules, page]);

    useEffect(() => {
        fetchLists();
    }, [fetchLists]);

    // A Free member who completes the GET POWER flow becomes Power without leaving the tab; refetch
    // so savedListsAccessStatus flips to Power and the dispatcher swaps the upsell for the hub +
    // default list. The popup fires this window event on a successful purchase.
    useEffect(() => {
        const handlePowerUpgradeSuccess = () => {
            fetchLists();
        };
        window.addEventListener(POWER_UPGRADE_SUCCESS_EVENT, handlePowerUpgradeSuccess);
        return () => window.removeEventListener(POWER_UPGRADE_SUCCESS_EVENT, handlePowerUpgradeSuccess);
    }, [fetchLists]);

    // A save through the global Save-to-List popup (e.g. the profile-header bookmark on the member's own
    // profile) changes list membership/counts without touching this hook; refetch so the hub reflects it.
    useEffect(() => {
        const handleSavedListsChanged = () => {
            fetchLists();
        };
        window.addEventListener(SAVED_LISTS_CHANGED_EVENT, handleSavedListsChanged);
        return () => window.removeEventListener(SAVED_LISTS_CHANGED_EVENT, handleSavedListsChanged);
    }, [fetchLists]);

    const applySearch = useCallback((value) => {
        setSearchValue(value);
        setPage(1);
    }, []);

    const applySort = useCallback((column, type) => {
        setSortColumn(column);
        setSortType(type);
        setPage(1);
    }, []);

    const deleteList = useCallback(
        listId => dispatch(deleteSavedPlayerList(listId)).then(() => {
            // Deleting the only row on a page past the first would strand the member on a now
            // out-of-range page; step back so the refetch lands on a page that still has rows.
            if (lists.length === 1 && page > 1) {
                setPage(page - 1);
            } else {
                fetchLists();
            }
        }),
        [dispatch, fetchLists, lists.length, page],
    );

    return {
        search: searchValue,
        sortColumn,
        sortType,
        page,
        lists,
        total,
        totalPages,
        totalPlayerCount,
        accessStatus,
        shouldShowNotificationOptIn,
        isLoading,
        isError,
        setSearch: applySearch,
        setSort: applySort,
        setPage,
        refetch: fetchLists,
        deleteList,
    };
};

export default useSavedPlayerLists;
